// CC: simplified mailbox list for onboarding wizard, taken from inbound-mailboxes.component.ts
import {eachLimit} from 'async';
import {GeneralService} from '../../../services/generalService';
import {RouteService} from '../../../services/routeService';
import {NotificationService} from '../../../services/notificationService';
import {DICTIONARY} from '../../../dictionary';
import {Component, EventEmitter, OnInit, Output} from "@angular/core";

@Component({
	selector: 'onboarding-mailboxes-component',
	templateUrl: './onboarding-mailboxes.component.html',
})
export class OnboardingMailboxesComponent implements OnInit {
	@Output() done = new EventEmitter<any>();

	dic = DICTIONARY;
	mailboxes = [];
	loadingInProcess = true;
	enableInProcess = false;
	selectedCount = 0;
	selectAll = false;
	searchTerm = '';
	sortBy = '';
	planInfo;
	graphConnected = false;
	gsuiteConnected = false;

	constructor(public gs: GeneralService,
				private rs: RouteService,
				private ns: NotificationService) {
	}

	// CC: taken from inbound-mailboxes.component.ts -> ngOnInit
	ngOnInit() {
		this.rs.getInboundPlanSettings().then((planInfo) => {
			this.planInfo = planInfo;

			if (planInfo.exchange_server?.tid && planInfo.exchange_server?.enabled) {
				this.graphConnected = true;
			}
			else if (planInfo.gsuite_server?.enabled) {
				this.gsuiteConnected = true;
			}

			this.getMailboxes();
		}, () => {
			this.loadingInProcess = false;
		});
	}

	// CC: taken from inbound-mailboxes.component.ts -> getMailboxesInfo
	getMailboxes = () => {
		this.loadingInProcess = true;
		this.rs.getMailboxesInfo().then((response) => {
			this.mailboxes = response || [];

			if (!this.mailboxes.length) {
				return this.getMailboxesFromExchange();
			}

			this.mailboxes.forEach((mailboxObj) => {
				if (mailboxObj.is_protected) {
					mailboxObj.status = this.dic.CONSTANTS.inboundMailBoxStatus.protect;
				}
				else {
					mailboxObj.status = this.dic.CONSTANTS.inboundMailBoxStatus.exposed;
				}
			});
			this.loadingInProcess = false;
		}, () => {
			this.loadingInProcess = false;
		});
	};

	// CC: taken from inbound-mailboxes.component.ts -> getMailboxesFromExchange + getExchangeMailboxesExecute
	getMailboxesFromExchange = () => {
		if (!this.graphConnected && !this.gsuiteConnected) {
			this.loadingInProcess = false;
			return;
		}

		const getUsers = this.gsuiteConnected ?
			this.rs.getGSuiteUsers() :
			this.rs.getExchangeUsers({nextLink: ''});

		getUsers.then(data => {
			data.mailboxes.forEach(user => {
				user.status = user.is_protected ? this.dic.CONSTANTS.inboundMailBoxStatus.protect : this.dic.CONSTANTS.inboundMailBoxStatus.exposed;
			});

			this.mailboxes = data.mailboxes;
			this.loadingInProcess = false;
		}, () => {
			this.loadingInProcess = false;
		});
	};

	searchMailboxes = () => {
		const term = this.searchTerm.toLowerCase();
		this.mailboxes.forEach(mailboxObj => {
			mailboxObj.hide = term && !(
				mailboxObj.name?.toLowerCase().includes(term) ||
				mailboxObj.email?.toLowerCase().includes(term) ||
				mailboxObj.license?.toLowerCase().includes(term)
			);
		});
	};

	// CC: taken from generalService.ts -> sortTable pattern
	sort = (field) => {
		if (this.sortBy === field) {
			this.sortBy = '-' + field;
		}
		else {
			this.sortBy = field;
		}
		this.gs.sortTable(this.mailboxes, this.sortBy);
	};

	toggleMailboxSelection = (mailboxObj) => {
		mailboxObj.selected = !mailboxObj.selected;
		this.updateSelectedCount();
	};

	updateSelectedCount = () => {
		this.selectedCount = this.mailboxes.filter(mailboxObj => mailboxObj.selected).length;
	};

	toggleSelectAll = () => {
		this.mailboxes.forEach(mailboxObj => {
			if (!mailboxObj.hide) {
				mailboxObj.selected = this.selectAll;
			}
		});
		this.updateSelectedCount();
	};

	// CC: called by onboarding wizard on Next — enables protection for all checked mailboxes
	// CC: skip only mailboxes already protected via API; mailboxes protected via relay/journal (is_fes) still need an API subscription
	enableCheckedMailboxes = () => {
		if (this.enableInProcess) {
			return;
		}
		const selected = this.mailboxes.filter(mailboxObj => mailboxObj.selected && !(mailboxObj.is_protected && !mailboxObj.is_fes));
		if (!selected.length) {
			this.done.emit();
			return;
		}
		this.enableInProcess = true;
		let completed = 0;
		let failed = 0;
		eachLimit(selected, 5, (mailboxObj, callback) => {
			const updateData = {
				action: this.dic.CONSTANTS.inboundMailboxAction.enable.value,
				email: mailboxObj.email
			};
			this.rs.updateMailboxInfo(updateData).then(() => {
				mailboxObj.is_protected = true;
				mailboxObj.status = this.dic.CONSTANTS.inboundMailBoxStatus.protect;
				completed++;
				callback();
			}, () => {
				failed++;
				callback();
			});
		}, () => {
			this.enableInProcess = false;
			if (completed) {
				this.ns.showInfoMessage(`Protection enabled for ${completed} mailbox${completed > 1 ? 'es' : ''}`);
			}
			if (failed) {
				this.ns.showWarnMessage(`Failed to enable protection for ${failed} mailbox${failed > 1 ? 'es' : ''}`);
				return;
			}
			this.done.emit();
		});
	};
}
